import { create, v, w } from '@dojo/framework/core/vdom';
import { createICacheMiddleware } from '@dojo/framework/core/middleware/icache';
import { theme } from '@dojo/framework/core/middleware/theme';
import Button from '@dojo/widgets/button';
import RadioGroup from '@dojo/widgets/radio-group';

// make generated .d.ts file valid
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { WNodeFactory, WidgetProperties, DNode } from '@dojo/framework/core/interfaces';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { ThemeProperties } from '@dojo/framework/core/middleware/theme';

import { userstate, quizPatternChangeProcess, createNlsMiddleware, landingChange } from 'lcc-state';
import { publish } from '@holmescorp/pub-sub';

import css from '../css/survey.m.css';

export interface SurveyProperties {}

interface surveyICache {
	yearsExperience: string;
	describeLevel: string;
	responsibilities: { [key: string]: string };
}

const nls = createNlsMiddleware('landing');
const icache = createICacheMiddleware<surveyICache>();
const factory = create({
	theme,
	icache,
	nls,
	userstate
}).properties<SurveyProperties>();

export const SurveyQuestions = factory(({ middleware: { theme, icache, nls, userstate } }) => {
	const themeCss = theme.classes(css);

	const yearsExperience = icache.getOrSet('yearsExperience', '');
	const describeLevel = icache.getOrSet('describeLevel', '');
	const responsibilities = icache.getOrSet('responsibilities', {});

	const {
		messages: { survey_button_text = '[landing] survey_button_text' }
	} = nls();

	const select_path = () => {
		const experienceLevels = icache.get('responsibilities');
		const years = icache.get('yearsExperience') || '';
		let experienceScore = 0;

		for (const level in experienceLevels) {
			const score = parseInt(experienceLevels[level]);
			experienceScore += score > 1 ? score : 0;
		}

		const quizPattern = years !== '0-2' && experienceScore >= 4 ? 'scp' : 'cp';

		userstate.run(quizPatternChangeProcess)({
			quizPattern
		});
		publish('change:quizpattern', quizPattern);

		save_responses();
	};

	const save_responses = () => {
		const responses = {
			yearsExperience: icache.get('yearsExperience') || '',
			describeLevel: icache.get('describeLevel') || '',
			responsibilities: icache.get('responsibilities') || ''
		};

		landingChange({ survey: responses });
	};

	const is_valid = () => {
		return (
			icache.get('yearsExperience') &&
			icache.get('describeLevel') &&
			Object.keys(icache.get('responsibilities') || {}).length === 4
		);
	};

	const questions = [
		v('fieldset', { classes: [themeCss.questionWrapper] }, [
			v('legend', { classes: [themeCss.required] }, ['How many years have you been in an HR role?']),
			w(RadioGroup, {
				name: 'years-experience',
				initialValue: yearsExperience,
				onValue: (value) => {
					icache.set('yearsExperience', value);
				},
				options: [
					{ value: '0-2', label: '0 to 2 years' },
					{ value: '3+', label: '3 or more years' }
				]
			})
		]),
		v('fieldset', { classes: [themeCss.questionWrapper] }, [
			v('legend', { classes: [themeCss.required] }, ['What best describes your HR career level?']),
			w(RadioGroup, {
				name: 'describe-level',
				initialValue: describeLevel,
				onValue: (value) => {
					icache.set('describeLevel', value);
				},
				options: [
					{
						value: 'early',
						label: 'Early (Specialist in a specific support function or a generalist with limited experience)'
					},
					{
						value: 'mid',
						label: 'Mid (Generalist or senior specialist who manages projects or programs)'
					},
					{ value: 'senior', label: 'Senior (Very experienced generalist or specialist)' },
					{ value: 'executive', label: 'Executive (Typically one of the most senior leaders in HR)' }
				]
			})
		]),
		v('fieldset', { classes: [themeCss.questionWrapper] }, [
			v('legend', { classes: [themeCss.required] }, [
				'For each statement, which most closely describes your day-to-day responsibilities?'
			]),
			w(RadioGroup, {
				name: 'hrPolicyExperience',
				initialValue: responsibilities['hrPolicy'] || '',
				options: [
					{ label: 'Implement HR Policies', value: '1' },
					{ label: 'Develop HR policies and/or procedures', value: '2' }
				],
				onValue: (value) => {
					responsibilities['hrPolicy'] = value;
					icache.set('responsibilities', responsibilities);
				}
			}),

			w(RadioGroup, {
				name: 'functionalExperience',
				initialValue: responsibilities['functional'] || '',
				options: [
					{ label: 'Support day-to-day HR functions', value: '1' },
					{ label: 'Oversee the execution of integrated HR operations', value: '2' }
				],
				onValue: (value) => {
					responsibilities['functional'] = value;
					icache.set('responsibilities', responsibilities);
				}
			}),

			w(RadioGroup, {
				name: 'roleExperience',
				initialValue: responsibilities['role'] || '',
				options: [
					{ label: 'Serve as an HR point of contact for staff and stakeholders', value: '1' },
					{ label: 'Direct the entire HR enterprise', value: '2' }
				],
				onValue: (value) => {
					responsibilities['role'] = value;
					icache.set('responsibilities', responsibilities);
				}
			}),

			w(RadioGroup, {
				name: 'leadershipExperience',
				initialValue: responsibilities['leadership'] || '',
				options: [
					{ label: 'Perform tactical or operational tasks under direct supervision', value: '1' },
					{ label: 'Lead the alignment of HR strategies to organizational goals', value: '2' }
				],
				onValue: (value) => {
					responsibilities['leadership'] = value;
					icache.set('responsibilities', responsibilities);
				}
			})
		]),
		v('div', {}, [w(Button, { disabled: !is_valid(), onClick: select_path }, [survey_button_text])])
	];

	return questions;
});

export default SurveyQuestions;
